/**
 * Utility puro para regra de fila de espera por categoria.
 *
 * Espelha a regra do backend (`InscricaoTorneioService.filaEsperaEfetiva` +
 * `determinarStatusInscricao` da Phase 2). Drift entre os dois lados é
 * coberto por defesa em profundidade: o backend rejeita com
 * `InscricaoNaoPermitidaException` qualquer inscrição que escape o
 * checkbox disabled do frontend.
 */

export interface CategoriaInscricaoFields {
  vagas?: number | null;
  inscricoesAtivasCount?: number | null;
  configFilaEspera?: boolean | null;
}

export interface TorneioInscricaoFields {
  filaEspera?: boolean | null;
}

export interface PodeSelecionarResultado {
  permitido: boolean;
  /** True quando a inscrição vai entrar em FILA_ESPERA (vagas esgotadas + fila efetiva ativa). */
  iraParaFilaEspera: boolean;
  /** Motivo do bloqueio (presente apenas quando `permitido === false`). */
  motivo?: string;
}

/**
 * Resolve a configuração efetiva de fila de espera para uma categoria.
 *
 * - `categoria.configFilaEspera` não-null tem precedência (override).
 * - `null` ou `undefined` herda de `torneio.filaEspera`.
 * - Defensivo: torneio ausente → false.
 */
export function filaEsperaEfetiva(
  categoria: CategoriaInscricaoFields,
  torneio: TorneioInscricaoFields,
): boolean {
  if (categoria.configFilaEspera !== null && categoria.configFilaEspera !== undefined) {
    return categoria.configFilaEspera;
  }
  return !!torneio.filaEspera;
}

/**
 * Decide se o atleta autenticado pode SELECIONAR a categoria para inscrição
 * a partir da tela `Tournamentsingle.tsx`, e se a inscrição (caso prossiga)
 * vai entrar em fila de espera.
 *
 * Importante: este utility cobre apenas a regra de fila (Phase 4 do PRD).
 * Outras razões de bloqueio (gênero, idade, período de inscrição) estão
 * explicitamente fora de escopo desta feature.
 *
 * Trade-off de precisão: `inscricoesAtivasCount` pode estar levemente
 * desatualizado no client (race entre fetch e submit). O backend
 * reforça a regra na mutation; o front é apenas a 1ª linha de defesa.
 */
export function podeSelecionarCategoria(
  categoria: CategoriaInscricaoFields,
  torneio: TorneioInscricaoFields,
): PodeSelecionarResultado {
  const vagas = categoria.vagas;
  const ocupadas = categoria.inscricoesAtivasCount ?? 0;

  // Categoria sem vagas definidas — comportamento preservado: sem limite,
  // nunca rebaixa para fila, nunca bloqueia (regra de borda do backend).
  if (vagas == null || vagas <= 0) {
    return { permitido: true, iraParaFilaEspera: false };
  }

  // Vaga disponível — inscrição segue como status normal.
  if (ocupadas < vagas) {
    return { permitido: true, iraParaFilaEspera: false };
  }

  // Vagas esgotadas — consulta regra efetiva.
  if (filaEsperaEfetiva(categoria, torneio)) {
    return { permitido: true, iraParaFilaEspera: true };
  }

  // Vagas esgotadas + fila desligada efetivamente → bloqueio.
  return {
    permitido: false,
    iraParaFilaEspera: false,
    motivo: 'Categoria sem vagas disponíveis',
  };
}
